import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MapPin, Mail, Clock, Send } from "lucide-react";
import { SITE } from "@/lib/site";
import SectionTitle from "@/components/SectionTitle";
import usePageMeta from "@/hooks/usePageMeta";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUBJECTS = [
  "Demande d'information",
  "Organisation d'obsèques",
  "Marbrerie & Ornements",
  "Prévoyance / Contrat obsèques",
  "Demande de devis",
  "Autre",
];

export default function ContactPage() {
  usePageMeta(
    "Contact & Devis gratuit — 06 24 29 91 00",
    "Contactez Pompes Funèbres Lumière des Nations à Baho. Devis gratuit, joignables 7j/7 — 24h/24 au 06 24 29 91 00. 14 rue du Ball, 66540 Baho."
  );
  const [form, setForm] = useState({
    nom: "",
    email: "",
    telephone: "",
    sujet: SUBJECTS[0],
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nom.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Merci de remplir les champs obligatoires.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message envoyé. Nous vous répondrons rapidement.");
      setForm({ nom: "", email: "", telephone: "", sujet: SUBJECTS[0], message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Une erreur est survenue. Merci de réessayer ou de nous appeler directement.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="relative bg-black text-white py-28 lg:py-40">
        <div className="relative container-soft">
          <p className="overline text-white/60">Contact & Devis gratuit</p>
          <h1 className="font-serif-display text-4xl sm:text-6xl lg:text-7xl mt-4 max-w-4xl tracking-tight leading-[1.02]">
            Une question, un besoin ? <em className="italic font-light">Nous sommes là.</em>
          </h1>
          <p className="mt-8 max-w-2xl text-white/80 text-lg leading-relaxed">
            Pour toute urgence, appelez directement. Pour une question ou un devis,
            écrivez-nous : un conseiller vous répondra dans les plus brefs délais.
          </p>
        </div>
      </section>

      <section className="py-24 lg:py-32">
        <div className="container-soft grid lg:grid-cols-12 gap-12">
          {/* INFOS */}
          <aside className="lg:col-span-5">
            <p className="overline">Coordonnées</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl mt-4 leading-tight">
              Joignables à toute heure, sans intermédiaire.
            </h2>

            <ul className="mt-12 space-y-px bg-black/10 border border-black/10">
              <li className="bg-white p-6 flex items-start gap-4">
                <Phone size={20} strokeWidth={1.25} className="mt-1 flex-none" />
                <div>
                  <p className="overline">Téléphone</p>
                  <a
                    href={SITE.phoneHref}
                    data-testid="contact-info-phone"
                    className="font-serif-display text-2xl mt-1 block hover:opacity-70"
                  >
                    {SITE.phone}
                  </a>
                </div>
              </li>
              <li className="bg-white p-6 flex items-start gap-4">
                <MapPin size={20} strokeWidth={1.25} className="mt-1 flex-none" />
                <div>
                  <p className="overline">Adresse</p>
                  <p className="font-serif-display text-2xl mt-1 leading-tight">{SITE.address}</p>
                </div>
              </li>
              <li className="bg-white p-6 flex items-start gap-4">
                <Mail size={20} strokeWidth={1.25} className="mt-1 flex-none" />
                <div>
                  <p className="overline">Email</p>
                  <a
                    href={`mailto:${SITE.email}`}
                    data-testid="contact-info-email"
                    className="font-serif-display text-xl mt-1 block hover:opacity-70 break-all"
                  >
                    {SITE.email}
                  </a>
                </div>
              </li>
              <li className="bg-white p-6 flex items-start gap-4">
                <Clock size={20} strokeWidth={1.25} className="mt-1 flex-none" />
                <div>
                  <p className="overline">Disponibilité</p>
                  <p className="font-serif-display text-2xl mt-1">{SITE.availability}</p>
                </div>
              </li>
            </ul>
          </aside>

          {/* FORM */}
          <div className="lg:col-span-7">
            <div className="border border-black/15 p-8 lg:p-12">
              <p className="overline">Devis gratuit</p>
              <h2 className="font-serif-display text-3xl sm:text-4xl mt-4 leading-tight">
                Écrivez-nous quelques lignes.
              </h2>
              <p className="mt-3 text-neutral-600">
                Réponse sous 24h. Pour toute urgence, merci d'appeler directement.
              </p>

              <form onSubmit={submit} data-testid="contact-form" className="mt-10 space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="nom" className="overline">Nom & prénom *</label>
                    <input
                      id="nom"
                      data-testid="contact-input-nom"
                      type="text"
                      required
                      value={form.nom}
                      onChange={update("nom")}
                      className="mt-3 w-full border-b border-black/30 focus:border-black bg-transparent py-3 text-base outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="overline">Email *</label>
                    <input
                      id="email"
                      data-testid="contact-input-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={update("email")}
                      className="mt-3 w-full border-b border-black/30 focus:border-black bg-transparent py-3 text-base outline-none transition-colors"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="telephone" className="overline">Téléphone</label>
                    <input
                      id="telephone"
                      data-testid="contact-input-telephone"
                      type="tel"
                      value={form.telephone}
                      onChange={update("telephone")}
                      className="mt-3 w-full border-b border-black/30 focus:border-black bg-transparent py-3 text-base outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="sujet" className="overline">Sujet</label>
                    <select
                      id="sujet"
                      data-testid="contact-input-sujet"
                      value={form.sujet}
                      onChange={update("sujet")}
                      className="mt-3 w-full border-b border-black/30 focus:border-black bg-transparent py-3 text-base outline-none transition-colors"
                    >
                      {SUBJECTS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="message" className="overline">Message *</label>
                  <textarea
                    id="message"
                    data-testid="contact-input-message"
                    required
                    rows={6}
                    value={form.message}
                    onChange={update("message")}
                    className="mt-3 w-full border border-black/15 focus:border-black bg-transparent p-4 text-base outline-none transition-colors resize-none"
                  />
                </div>

                <button
                  type="submit"
                  data-testid="contact-form-submit"
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-3 bg-black text-white px-8 py-5 rounded-sm text-sm tracking-wide hover:bg-neutral-800 transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-wait"
                >
                  {loading ? "Envoi…" : (<><Send size={16} strokeWidth={1.5} /> Envoyer le message</>)}
                </button>

                <p className="text-xs text-neutral-500 mt-2">
                  Vos données restent confidentielles et ne sont utilisées que pour répondre à votre demande.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* MAP */}
      <section data-testid="map-section" className="border-t border-black/10">
        <div className="container-soft py-24 lg:py-32">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4">
              <p className="overline">Nous trouver</p>
              <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl mt-4 leading-[1.08]">
                Au cœur de Baho, à deux pas de Perpignan.
              </h2>
              <ul className="mt-10 space-y-3 text-neutral-700">
                <li className="flex justify-between gap-4 pb-3 border-b border-black/10">
                  <span className="text-neutral-500 text-sm">Adresse</span>
                  <span className="text-right">{SITE.address}</span>
                </li>
                <li className="flex justify-between gap-4 pb-3 border-b border-black/10">
                  <span className="text-neutral-500 text-sm">Téléphone</span>
                  <a href={SITE.phoneHref} className="hover:opacity-70">{SITE.phone}</a>
                </li>
                <li className="flex justify-between gap-4 pb-3 border-b border-black/10">
                  <span className="text-neutral-500 text-sm">Disponibilité</span>
                  <span>{SITE.availability}</span>
                </li>
              </ul>
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(SITE.address)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="map-directions-link"
                className="mt-10 inline-flex items-center justify-center gap-2 border border-black px-6 py-4 rounded-sm text-sm tracking-wide hover:bg-black hover:text-white transition-all duration-300"
              >
                Itinéraire vers l'agence
              </a>
            </div>
            <div className="lg:col-span-8">
              <div className="border border-black/15 overflow-hidden">
                <iframe
                  data-testid="map-iframe"
                  title="Plan — Pompes Funèbres Lumière des Nations, 14 rue du Ball, 66540 Baho"
                  src={`https://www.google.com/maps?q=${encodeURIComponent(SITE.address)}&z=15&output=embed`}
                  width="100%"
                  height="480"
                  style={{ border: 0, filter: "grayscale(0.6) contrast(1.05)" }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-black text-white py-20">
        <div className="container-soft text-center">
          <SectionTitle
            overline="Urgence décès"
            title="Un appel, et nous prenons le relais."
            align="center"
            invert
          />
          <a
            href={SITE.phoneHref}
            data-testid="contact-urgent-call"
            className="mt-10 inline-flex items-center justify-center gap-3 bg-white text-black px-8 py-5 rounded-sm text-sm tracking-wide hover:bg-neutral-200 transition-all duration-300"
          >
            <Phone size={18} strokeWidth={1.5} /> {SITE.phone}
          </a>
        </div>
      </section>
    </div>
  );
}
